import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Spinner, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Download } from '@factorialco/f0-react/icons/app'
import {
  type DataHandlerResultValues,
  type EntryChangeResult,
  type ImportLogs,
  MappingLayer,
  type Modifier,
  NuvoImporter as RawNuvoImporter,
  RejectSubmitResult,
  type RowResult,
} from '@getnuvo/importer-react'
import * as Sentry from '@sentry/browser'
import { EnvironmentContext } from 'config/environment'
import { UniversalImporterValueObjectsImportTypeEnum } from 'generated/resources/datatable.graphql'
import { NuvoImporterDocument, UniversalImporterProcessDocument } from 'generated/resources/graphql'

import { Authorized } from 'modules/core/components/Authorized'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'
import useDownloadTemplate from 'modules/employees/hooks/useDownloadTemplate'
import { i18nOverrides } from 'modules/universal_importer/components/NuvoImporter/locales'
import { getStyle } from 'modules/universal_importer/components/NuvoImporter/styles'
import {
  type EditionData,
  Metadata,
} from 'modules/universal_importer/components/NuvoImporter/types'
import {
  type ColumnCustomization,
  useColumnCustomizations,
} from 'modules/universal_importer/components/NuvoImporter/useColumnCustomizations'
import { useColumnsMapping } from 'modules/universal_importer/components/NuvoImporter/useColumnsMapping'
import { useEditionData } from 'modules/universal_importer/components/NuvoImporter/useEditionData'
import { useFileUpload } from 'modules/universal_importer/components/NuvoImporter/useFileUpload'
import { useGetMetadata } from 'modules/universal_importer/components/NuvoImporter/useGetMetadata'
import { useLanguage } from 'modules/universal_importer/components/NuvoImporter/useLanguage'
import { useNavigationHandling } from 'modules/universal_importer/components/NuvoImporter/useNavigationHandling'
import { useNuvoSession } from 'modules/universal_importer/components/NuvoImporter/useNuvoSession'
import { useSendImportResults } from 'modules/universal_importer/components/NuvoImporter/useSendingImportResults'
import {
  createRowResult,
  toCompatibleColumns,
  toModifierColumn,
} from 'modules/universal_importer/lib/helpers'
import { transformationFunctions } from 'modules/universal_importer/lib/transformations'
import { validationFunctions } from 'modules/universal_importer/lib/validations'

type ProcessBy = 'rows' | 'columns'

const columnMappingLayers: MappingLayer[] = ['historic', 'exact', 'smart', 'fuzzy']
const optionMappingLayers: MappingLayer[] = ['exact', 'fuzzy']
const defaultThresholdMapping = 0.8

const downloadPath = (
  importer_type: UniversalImporterValueObjectsImportTypeEnum,
  metadata: Metadata = {}
) => {
  const queryParams = new URLSearchParams()
  queryParams.append('metadata', JSON.stringify(metadata))
  return `/universal_importer/templates/${importer_type}?${queryParams.toString()}`
}

type Props = {
  importType: UniversalImporterValueObjectsImportTypeEnum
  headerSelection?: boolean
  companyId: number
  metadata?: Metadata
  locale?: string
  editionData?: EditionData
  customBackNavigation?: string
  customFinishNavigation?: string
  customLoading?: React.ReactNode
  isEdition?: boolean
  forceCreateAction?: boolean
  parentProcessId?: number | null
  rootProcessId?: number | null
  onLoading?: (loading: boolean) => void
  onUploadedFile?: (file: File) => void
  onDownloadTemplate?: () => void
  onCompleted?: () => void
  onError?: (error_message?: string) => void
  processBy?: ProcessBy
  thresholdMapping?: number
}

const NuvoImporter = ({
  importType,
  headerSelection = false,
  companyId,
  metadata = {},
  locale = 'en-US',
  customBackNavigation,
  customFinishNavigation,
  customLoading,
  isEdition,
  forceCreateAction,
  parentProcessId,
  rootProcessId,
  onLoading,
  onUploadedFile,
  onDownloadTemplate,
  onCompleted,
  onError,
  processBy = 'rows',
  thresholdMapping = defaultThresholdMapping,
}: Props) => {
  const { nuvoApiKey, nuvoDeveloperMode } = React.useContext(EnvironmentContext)
  const navigate = useNavigate()
  const { sendImportResults } = useSendImportResults()
  const {
    access: currentUser,
    company: { subscription },
  } = useCurrentSession()
  const [hasChanges, setHasChanges] = React.useState(false)
  const nuvoImporterRef = React.useRef<HTMLDivElement>(null)
  const { metadataInput, legalEntitiesIds } = useGetMetadata(metadata)
  const { data, loading } = useQuery(NuvoImporterDocument, {
    variables: { companyId, importType, metadata: metadataInput },
  })
  const { downloading, onDownload, error, value } = useDownloadTemplate({
    path: downloadPath(importType, metadata),
  })
  const [initialData, setInitialData] = React.useState<EditionData | undefined>(undefined)
  const [originalFile, setOriginalFile] = React.useState<File | undefined>(undefined)
  const isDownloadingRef = React.useRef(false)
  const tracker = React.useMemo(
    () => ({
      id: crypto.randomUUID(),
      refresh() {
        this.id = crypto.randomUUID()
      },
    }),
    []
  )
  const commonEventProperties = useUnsafeMemo(
    () => ({
      user_id: currentUser.id,
      company_id: companyId,
      legal_entity_ids: legalEntitiesIds,
      import_type: importType,
      tracker_id: tracker.id,
    }),
    [currentUser.id, companyId, legalEntitiesIds, importType, tracker.id]
  )
  const { parsedFile, parseFileLoading, handleFileUpload } = useFileUpload({
    onFileUpload: (file) => {
      track(trackingEvents.universalImporter.imports.attachFile, {
        file_name: file.name,
        file_size: file.size,
        file_type: file.type,
        ...commonEventProperties,
      })
      setOriginalFile(file)
      onUploadedFile?.(file)
    },
  })
  useNuvoSession({
    importType,
    parsedFile,
    initialData,
    loading,
    nuvoImporterRef,
    headerSelection,
    transpose: processBy === 'columns',
  })
  const { bypassAlertNavigationConfirmation } = useNavigationHandling({
    isDownloadingRef,
    navigate,
    confirmExitMessage: i18n.t('universal_importer.nuvo_importer.confirm_exit'),
    customBackNavigation,
  })
  const { language } = useLanguage({ locale })
  const fix = metadata?.import_process_ids
  const editionOrFix = isEdition || fix
  // Controls what isEdition value is sent to backend (forceCreateAction sends false even when loading from DB)
  const effectiveIsEdition = forceCreateAction ? false : !!isEdition
  // Combines effectiveIsEdition with fix for UI elements (style, i18nOverrides)
  const effectiveEditionOrFix = effectiveIsEdition || fix
  const { columns } = useColumnsMapping({
    data,
    optionMappingConfiguration: {
      layers: optionMappingLayers,
      threshold: thresholdMapping,
      processingMode: 'browser',
    },
    isEdition: !!editionOrFix,
  })
  const { columns: customizations } = useColumnCustomizations({ data })

  const { data: importProcessData } = useQuery(UniversalImporterProcessDocument, {
    variables: { importProcessId: Number(parentProcessId) },
    skip: !parentProcessId,
  })

  const totalRows = importProcessData?.universalImporter.importProcess?.total ?? 0

  if (fix) {
    metadata['is_fix'] = [true]
  }

  const { editionData, loading: editionLoading } = useEditionData({
    companyId,
    importType,
    metadata: metadataInput,
    isEdition: !!editionOrFix,
  })

  const handleOnDownload = () => {
    isDownloadingRef.current = true
    onDownload()
    onDownloadTemplate?.()
    track(trackingEvents.universalImporter.imports.downloadTemplate, {
      error: error && value?.toString(),
      ...commonEventProperties,
    })
  }

  // Reset download state when downloading changes
  React.useEffect(() => {
    if (!downloading) {
      isDownloadingRef.current = false
    }
  }, [downloading])

  useUnsafeEffect(() => {
    if (!loading && editionOrFix) {
      setInitialData(editionData)
    }
  }, [editionData, loading, editionOrFix])

  useUnsafeEffect(() => {
    onLoading?.(loading || editionLoading)
  }, [loading, editionLoading, onLoading])

  if (loading || editionLoading)
    return (
      customLoading ?? (
        <Box justifyContent="center" alignItems="center" height="full">
          <Spinner />
        </Box>
      )
    )

  // Filter columns to only include those that match editionData keys
  const firstEditionRow = editionData[0]

  // Analyze key count for each row to detect structure variations
  const rowKeyCounts = editionData.map((row, index) => ({
    rowIndex: index,
    keyCount: Object.keys(row || {}).length,
    keys: Object.keys(row || {}),
  }))

  const allColumnKeys = new Set(columns.map((col) => col.key))
  const firstRowKeys = new Set(Object.keys(firstEditionRow || {}))
  const filteredColumnKeys = new Set([...allColumnKeys].filter((key) => firstRowKeys.has(key)))

  const filteredColumns = firstEditionRow
    ? columns.filter((col) => filteredColumnKeys.has(col.key))
    : columns

  const onlyInFirstRow = [...firstRowKeys].filter((key) => !allColumnKeys.has(key))
  const onlyInColumns = [...allColumnKeys].filter((key) => !firstRowKeys.has(key))
  const onlyInFilteredColumns = [...filteredColumnKeys].filter((key) => !firstRowKeys.has(key))
  const hasVariableStructure = new Set(rowKeyCounts.map((r) => r.keyCount)).size > 1

  if (
    (onlyInFirstRow.length > 0 ||
      onlyInFilteredColumns.length > 0 ||
      onlyInColumns.length > 0 ||
      hasVariableStructure) &&
    editionOrFix
  ) {
    const extra = {
      onlyInFirstRow: [...onlyInFirstRow],
      onlyInFilteredColumns: [...onlyInFilteredColumns],
      onlyInColumns: [...onlyInColumns],
      firstRowKeysCount: firstRowKeys.size,
      filteredColumnsKeysCount: filteredColumnKeys.size,
      commonKeysCount: [...firstRowKeys].filter((key) => filteredColumnKeys.has(key)).length,
      firstRowKeys: [...firstRowKeys],
      allColumnKeys: [...allColumnKeys],
      filteredColumnKeys: [...filteredColumnKeys],
      // Row structure analysis
      totalEditionRows: editionData.length,
      keyCountDistribution: rowKeyCounts.reduce<Record<number, number>>((acc, row) => {
        acc[row.keyCount] = (acc[row.keyCount] || 0) + 1
        return acc
      }, {}),
      examplesByKeyCount: Object.fromEntries(
        Array.from(new Set(rowKeyCounts.map((r) => r.keyCount))).map((keyCount) => {
          const example = rowKeyCounts.find((r) => r.keyCount === keyCount)
          return [
            keyCount,
            example
              ? {
                  ...example,
                  keys: [...example.keys], // Expand the keys array for proper Sentry serialization
                }
              : null,
          ]
        })
      ),
      hasVariableStructure: new Set(rowKeyCounts.map((r) => r.keyCount)).size > 1,
      minKeyCount: rowKeyCounts.length > 0 ? Math.min(...rowKeyCounts.map((r) => r.keyCount)) : 0,
      maxKeyCount: rowKeyCounts.length > 0 ? Math.max(...rowKeyCounts.map((r) => r.keyCount)) : 0,
    }

    Sentry.captureMessage(
      `NuvoImporter: Key differences analysis - Company ${companyId}, Type ${importType}`,
      {
        level: 'info',
        tags: {
          component: 'NuvoImporter',
          operation: 'key-comparison',
        },
        extra: extra,
      }
    )
  }

  // Add errors column as disabled if in fix mode
  const columnsWithErrors = fix
    ? [
        {
          key: 'errors',
          label: 'Reason',
          columnType: 'string' as const,
          disabled: true,
        },
        ...filteredColumns,
      ]
    : filteredColumns

  return (
    <Box gap="s40" extra={{ 'data-intercom-target': 'universal-importer-nuvo-importer' }}>
      {!editionOrFix && (
        <>
          <Authorized subscription={subscription}>
            <DropzoneBox
              onDrop={(files: File[]) => handleFileUpload(files)}
              iconName="common-file-text-upload"
              message={
                parsedFile?.length && parsedFile[0]?.fileName
                  ? parsedFile[0].fileName
                  : i18n.t('universal_importer.nuvo_importer.upload_file.message')
              }
              description={i18n.t('universal_importer.nuvo_importer.upload_file.description')}
              multiple={false}
              accept={{
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
                'application/vnd.ms-excel': [],
                'text/csv': [],
                'application/csv': [],
              }}
              maxSize={4}
              disabled={parseFileLoading}
            />
          </Authorized>
          <Box flexDirection="column" alignItems="flexStart">
            <Text.Normal weight="semibold">
              {i18n.t('universal_importer.nuvo_importer.download_template.title')}
            </Text.Normal>
            <Text.Normal>
              {i18n.t('universal_importer.nuvo_importer.download_template.description')}
            </Text.Normal>
            <Box paddingTop="s8">
              <F0Button
                onClick={handleOnDownload}
                size="md"
                variant="neutral"
                label={i18n.t('universal_importer.nuvo_importer.download_template.link')}
                loading={downloading}
                icon={Download}
                data-intercom-target="universal-importer-nuvo-importer-download-template"
              />
            </Box>
          </Box>
        </>
      )}
      <Box ref={nuvoImporterRef}>
        <RawNuvoImporter
          licenseKey={nuvoApiKey}
          settings={{
            language,
            developerMode: nuvoDeveloperMode,
            identifier: importType,
            // @ts-expect-error - DropdownOption type compatibility with Nuvo DropdownOptionAPI
            columns: columnsWithErrors,
            onlyMappedColumns: !editionOrFix,
            automaticHeaderDetection: !headerSelection,
            disableSuccessModal: true,
            completeImportAction: 'block',
            smartTable: true,
            style: getStyle(!!editionOrFix, editionData?.length),
            i18nOverrides: i18nOverrides(!!effectiveEditionOrFix),
            columnMappingConfiguration: {
              layers: columnMappingLayers,
            },
          }}
          dataHandler={{
            headerStep: async () => {
              track(trackingEvents.universalImporter.imports.headerStep, commonEventProperties)
            },
            reviewStep: async (
              modifier: Modifier,
              reviewData: DataHandlerResultValues,
              _logs?: ImportLogs
            ): Promise<DataHandlerResultValues> => {
              track(trackingEvents.universalImporter.imports.reviewStep, commonEventProperties)
              // Add new columns dynamically and track their keys
              const dynamicallyAddedColumns: string[] = []
              customizations
                .filter((column: ColumnCustomization) =>
                  column.customizations?.some((c) => c.type === 'transformation')
                )
                .forEach((column: ColumnCustomization) => {
                  if (column.hidden) {
                    modifier.addColumn({
                      key: column.key,
                      label: column.label,
                      columnType: column.columnType,
                      hidden: column.hidden ? (column.disabled ? false : true) : false,
                      disabled: column.disabled,
                      validations: column.validations || undefined,
                    })
                    dynamicallyAddedColumns.push(column.key)
                  }
                })

              //TODO: Refactor all this logic when migrating to Nuvo 3.0.0 as we will haave alerts and blocks to give feedback to the user
              //  Obtain the mapped columns
              const mappedColumnKeys = new Set(reviewData[0] ? Object.keys(reviewData[0]) : [])

              // Calculate all columns that are included in 'required_with' or 'required_with_all' validations of mapped columns
              const allRequiredColumns = [
                ...new Set(
                  customizations
                    .filter(
                      (col) =>
                        mappedColumnKeys.has(col.key) &&
                        col.customizations?.some(
                          (c) =>
                            c.type === 'validation' &&
                            (c.computeWith === 'required_with' ||
                              c.computeWith === 'required_with_all')
                        )
                    )
                    .flatMap(
                      (col) =>
                        col.customizations
                          ?.filter(
                            (c) =>
                              c.type === 'validation' &&
                              (c.computeWith === 'required_with' ||
                                c.computeWith === 'required_with_all')
                          )
                          .flatMap((c) => {
                            // Extract columns from parameters
                            const columnsParam = c.parameters?.find(
                              (p) => p.key === 'columns'
                            )?.value
                            return columnsParam
                              ? columnsParam.split(',').map((colName) => colName.trim())
                              : []
                          }) || []
                    )
                ),
              ]

              // Calculate the missing columns using the first row
              const missingColumns = allRequiredColumns.filter(
                (colName) => !reviewData[0]?.hasOwnProperty(colName)
              )

              // Add missing columns dynamically
              missingColumns.forEach((colName) => {
                const column = columns.find((col) => col.key === colName)
                if (column && column.columnType) {
                  modifier.addColumn(toModifierColumn(column))
                }
              })
              //END- TODO

              // Combine dynamically added columns with missing columns
              const allColumnsToInitialize = [
                ...new Set([...dynamicallyAddedColumns, ...missingColumns]),
              ]

              if (customizations.length === 0 && allColumnsToInitialize.length === 0) {
                return reviewData
              }

              const newData = reviewData.map((row) => {
                // Add the dynamically added columns and missing columns with null values
                allColumnsToInitialize.forEach((colName) => {
                  row[colName] = null
                })

                // Apply existing transformations and validations
                customizations.forEach((column: ColumnCustomization) => {
                  if (column.customizations) {
                    column.customizations.forEach((customization) => {
                      // Dynamically call the transformation function based on computeWith
                      const transformationFunction =
                        transformationFunctions[customization.computeWith]
                      if (transformationFunction && row.hasOwnProperty(column.key)) {
                        // Apply the transformation and populate the target column
                        const result = transformationFunction(
                          row,
                          metadata,
                          toCompatibleColumns(columns),
                          column.key,
                          customization.parameters
                        )
                        // Set the computed value to the target column key
                        row[column.key] = result.value
                      }
                      // Dynamically call the validation function based on computeWith
                      const validationFunction = validationFunctions[customization.computeWith]
                      if (validationFunction && row.hasOwnProperty(column.key)) {
                        // Apply the validation and populate the target column
                        const result = validationFunction(
                          row,
                          metadata,
                          toCompatibleColumns(columns),
                          column.key,
                          customization.parameters
                        )
                        // Set the computed error to the target column key
                        row[column.key] = result.value
                      }
                    })
                  }
                })

                return {
                  ...row,
                }
              })
              return newData
            },
          }}
          onCancel={() => {
            track(trackingEvents.universalImporter.imports.cancelled, commonEventProperties)
            tracker.refresh()
            bypassAlertNavigationConfirmation()
            if (customBackNavigation) {
              navigate(customBackNavigation)
            } else {
              navigate(-1)
            }
          }}
          onEntryChange={(rows, _logs) => {
            if (isEdition && !fix && !hasChanges) {
              setHasChanges(true)
            }

            // Early return if no transformations to apply
            if (customizations.length === 0) {
              return
            }

            // Transform rows to correct EntryChangeResult[] format
            const entryChangeResults: EntryChangeResult[] = rows.map((rowData) => {
              const { data: row, actionType, rowIndex: rowIndex, changeLog: changeLog } = rowData

              // Create clean RowResult object for this row
              const cleanRow: RowResult = { ...row }

              if (actionType === 'delete') {
                return {
                  rowIndex: rowIndex,
                  data: cleanRow,
                  actionType,
                }
              }

              customizations.forEach((column: ColumnCustomization) => {
                if (column.customizations) {
                  column.customizations.forEach((customization) => {
                    // Dynamically call the transformation function based on computeWith
                    const transformationFunction =
                      transformationFunctions[customization.computeWith]
                    if (transformationFunction && row.hasOwnProperty(column.key)) {
                      const result = transformationFunction(
                        row,
                        metadata,
                        toCompatibleColumns(columns),
                        column.key,
                        customization.parameters
                      )
                      // Store as RowResult (FieldValue or HookedRecordResult)
                      cleanRow[column.key] = createRowResult(result.value)
                    }

                    // Dynamically call the validation function based on computeWith
                    const validationFunction = validationFunctions[customization.computeWith]
                    if (validationFunction && row.hasOwnProperty(column.key)) {
                      const result = validationFunction(
                        cleanRow,
                        metadata,
                        toCompatibleColumns(columns),
                        column.key,
                        customization.parameters,
                        changeLog
                      )
                      // Store as RowResult (FieldValue or HookedRecordResult)
                      cleanRow[column.key] = createRowResult(result.value)
                    }
                  })
                }
              })

              // Return EntryChangeResult with correct structure
              return {
                rowIndex: rowIndex,
                data: cleanRow,
                actionType,
              }
            })

            return entryChangeResults
          }}
          onResults={async (result, errors, complete) => {
            if (!result.length) {
              complete(
                new RejectSubmitResult(
                  i18n.t('universal_importer.nuvo_importer.results.no_rows_alert.title'),
                  i18n.t('universal_importer.nuvo_importer.results.no_rows_alert.description')
                )
              )
              return
            }

            track(trackingEvents.universalImporter.imports.processRequested, {
              total_rows: result.length,
              rows_with_errors: errors.length,
              ...commonEventProperties,
            })

            if (isEdition && !fix && !hasChanges) {
              complete(
                new RejectSubmitResult(
                  i18n.t('universal_importer.nuvo_importer.results.no_changes_alert.title'),
                  i18n.t('universal_importer.nuvo_importer.results.no_changes_alert.description')
                )
              )
              return
            }

            const response = await sendImportResults({
              data: result,
              metadata,
              companyId,
              importType,
              parentProcessId,
              rootProcessId,
              isEdition: effectiveIsEdition,
              originalFile,
            })

            if (response.success) {
              const columnNames = Object.keys(result?.[0] || {})

              const columnPercentages = columnNames
                .map((columnName) => {
                  const columnValues = result.map((row) => row[columnName])
                  const percentage =
                    (columnValues.filter(Boolean).length / columnValues.length) * 100
                  return { [columnName]: percentage.toFixed(2) }
                })
                .reduce((acc, curr) => ({ ...acc, ...curr }), {})
              if (fix) {
                track(trackingEvents.universalImporter.imports.startedFixErrors, {
                  total_rows: totalRows,
                  ...commonEventProperties,
                  import_process_id: response.value.id,
                  total_failed_rows: result.length,
                })
              } else {
                track(trackingEvents.universalImporter.imports.started, {
                  total_rows: result.length,
                  ...commonEventProperties,
                  import_process_id: response.value.id,
                  column_percentages: JSON.stringify(columnPercentages),
                })
              }

              bypassAlertNavigationConfirmation()
              onCompleted?.()
              if (customFinishNavigation) {
                navigate(customFinishNavigation)
              } else {
                navigate(-1)
              }
            } else {
              const errorMessage = response.value.message || ''
              track(trackingEvents.universalImporter.imports.processRequestError, {
                total_rows: result.length,
                error_message:
                  errorMessage.length > 500 ? `${errorMessage.slice(0, 500)}...` : errorMessage,
                ...commonEventProperties,
              })

              onError?.(response.value.message)

              complete(
                new RejectSubmitResult(
                  i18n.t('universal_importer.nuvo_importer.results.api_response_alert.title'),
                  i18n.t('universal_importer.nuvo_importer.results.api_response_alert.description')
                )
              )
            }
          }}
        />
      </Box>
    </Box>
  )
}

export default NuvoImporter
